import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar/Navbar';
import Footer from '../../../../components/Footer/Footer';
import '../../../../styles/Events.css';
import '../../../../styles/Pages.css';

const API = `${process.env.REACT_APP_API_URL}/api`;

function AdminPolls() {
    const [polls, setPolls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        title: '', description: '', category: 'Farewell',
        optionNames: '', endTime: '', startTime: '',
    });
    const [formMsg, setFormMsg] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);

    const fetchPolls = useCallback(() => {
        setLoading(true);
        fetch(`${API}/admin/polls`, { headers })
            .then(r => r.json())
            .then(data => {
                if (data.success) setPolls(data.polls);
                else setError(data.message);
            })
            .catch(() => setError('Failed to load polls.'))
            .finally(() => setLoading(false));
    }, [headers]);

    useEffect(() => { fetchPolls(); }, [fetchPolls]);

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true); setFormMsg('');
        try {
            const res = await fetch(`${API}/admin/polls`, {
                method: 'POST', headers,
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (data.success) {
                setFormMsg('✅ Poll created!');
                setShowForm(false);
                setForm({ title: '', description: '', category: 'Farewell', optionNames: '', endTime: '', startTime: '' });
                fetchPolls();
            } else {
                setFormMsg(`❌ ${data.message}`);
            }
        } catch { setFormMsg('❌ Network error.'); }
        finally { setSubmitting(false); }
    };

    const toggleActive = async (poll) => {
        await fetch(`${API}/admin/polls/${poll._id}`, {
            method: 'PUT', headers,
            body: JSON.stringify({ isActive: !poll.isActive }),
        });
        fetchPolls();
    };

    const deletePoll = async (id) => {
        if (!window.confirm('Delete this poll and all its votes?')) return;
        await fetch(`${API}/admin/polls/${id}`, { method: 'DELETE', headers });
        fetchPolls();
    };

    const resetVotes = async (id) => {
        if (!window.confirm('Reset all votes for this poll?')) return;
        await fetch(`${API}/admin/polls/${id}/reset`, { method: 'POST', headers });
        fetchPolls();
    };

    const getStatus = (poll) => {
        const now = new Date();
        if (!poll.isActive) return { label: '⚪ Inactive', cls: 'inactive' };
        if (new Date(poll.endTime) < now) return { label: '🔴 Ended', cls: 'ended' };
        if (new Date(poll.startTime) > now) return { label: '🟡 Scheduled', cls: 'scheduled' };
        return { label: '🟢 Live', cls: 'live' };
    };

    return (
        <div className="page-wrapper">
            <Navbar />
            <div className="page-hero">
                <h1>🗳️ Poll Management</h1>
                <p>Create & manage farewell award polls for students.</p>
            </div>
            <div className="page-content">

                {/* Header Actions */}
                <div className="admin-polls-header">
                    <h2 className="admin-polls-count">{polls.length} Poll{polls.length !== 1 ? 's' : ''} Total</h2>
                    <button className="create-poll-btn" onClick={() => setShowForm(!showForm)}>
                        {showForm ? '✕ Cancel' : '+ Create New Poll'}
                    </button>
                </div>

                {formMsg && <div className={`poll-msg ${formMsg.startsWith('✅') ? 'success' : 'error'}`}>{formMsg}</div>}

                {/* Create Poll Form */}
                {showForm && (
                    <div className="admin-poll-form-box">
                        <h3>Create a New Poll</h3>
                        <form onSubmit={handleCreate}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Poll Title *</label>
                                    <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Best Attitude Award" required />
                                </div>
                                <div className="form-group">
                                    <label>Category</label>
                                    <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g. Farewell, Sports" />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Description (optional)</label>
                                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Short description of this poll" />
                            </div>
                            <div className="form-group">
                                <label>Candidate Names * <small>(comma-separated, e.g.: Rahul, Priya, Amit)</small></label>
                                <textarea
                                    value={form.optionNames}
                                    onChange={e => setForm({ ...form, optionNames: e.target.value })}
                                    placeholder="Rahul Sharma, Priya Patel, Amit Kumar, Sneha Jadhav"
                                    rows={3}
                                    required
                                />
                                {form.optionNames && (
                                    <div className="names-preview">
                                        <strong>Options preview:</strong>{' '}
                                        {form.optionNames.split(',').map(n => n.trim()).filter(Boolean).map((n, i) => (
                                            <span key={i} className="name-chip">{n}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Start Time <small>(leave blank to start immediately)</small></label>
                                    <input type="datetime-local" value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>End Time *</label>
                                    <input type="datetime-local" value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} required />
                                </div>
                            </div>
                            <button type="submit" className="submit-btn" disabled={submitting}>
                                {submitting ? 'Creating…' : '🗳️ Create Poll'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Poll List */}
                {loading && <div className="events-loading"><div className="events-spinner"></div><p>Loading…</p></div>}
                {error && <div className="events-error">{error}</div>}

                {!loading && polls.length === 0 && (
                    <div className="events-empty">
                        <span>🗳️</span>
                        <h3>No polls yet</h3>
                        <p>Click "Create New Poll" to get started.</p>
                    </div>
                )}

                <div className="admin-polls-list">
                    {polls.map(poll => {
                        const { label, cls } = getStatus(poll);
                        const totalVotes = poll.options.reduce((s, o) => s + o.votes, 0);
                        return (
                            <div key={poll._id} className={`admin-poll-card status-${cls}`}>
                                <div className="apc-top">
                                    <div>
                                        <span className={`poll-status-badge ${cls}`}>{label}</span>
                                        <h3 className="apc-title">{poll.title}</h3>
                                        <p className="apc-meta">
                                            {poll.category} &bull; {poll.options.length} candidates &bull; {totalVotes} total votes
                                        </p>
                                        <p className="apc-time">
                                            Ends: {new Date(poll.endTime).toLocaleString('en-IN')}
                                        </p>
                                    </div>
                                    <div className="apc-actions">
                                        <Link to={`/admin/polls/${poll._id}/results`} className="apc-btn results">📊 Results</Link>
                                        <button onClick={() => toggleActive(poll)} className={`apc-btn ${poll.isActive ? 'deactivate' : 'activate'}`}>
                                            {poll.isActive ? '⏸ Deactivate' : '▶ Activate'}
                                        </button>
                                        <button onClick={() => resetVotes(poll._id)} className="apc-btn reset">🔄 Reset Votes</button>
                                        <button onClick={() => deletePoll(poll._id)} className="apc-btn delete">🗑 Delete</button>
                                    </div>
                                </div>
                                {/* Mini options preview */}
                                <div className="apc-options-strip">
                                    {poll.options.slice(0, 8).map((o, i) => (
                                        <span key={i} className="apc-option-chip">{o.label}</span>
                                    ))}
                                    {poll.options.length > 8 && <span className="apc-option-chip more">+{poll.options.length - 8} more</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default AdminPolls;
